import { useEffect, useRef, useState } from 'react';

// material-ui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// project imports
import { captureSignature } from 'api/signatures';
import PinKeypad from 'components/PinKeypad';
import { PIN_PATTERN } from 'utils/pinConfig';

// assets
import { IconCamera, IconDelete } from 'utils/icons';

// ==============================|| CORE - SIGNATURE CAPTURE ||============================== //
// Reusable "podpis" building block (docs/ARCHITECTURE.md "Signatures - new
// core-level component") - explicitně mobile-first touch signing (žádný
// smysluplný desktop ekvivalent, ale funguje i myší). Kreslení přes plain
// Canvas + Pointer Events (funguje pro myš i dotyk jedním kódem, žádná
// externí knihovna) - PIN se ověřuje a podpis ukládá teprve na serveru
// (SignatureService::capture()), tahle komponenta jen sbírá vstup.
//
// requirePhoto (2026-08-23, podle schválené specifikace Docházkového
// systému - formální toky vyžadují PIN + podpis + foto) - když je true,
// appka požádá o fotku přes kameru (input capture="environment") před
// odesláním; kdy přesně requirePhoto=true dávat rozhoduje volající (např.
// běžné potvrzení vs. schválení neplaceného volna).

function useSignaturePad() {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const [hasInkState, setHasInkState] = useState(false);

  const getCanvas = () => canvasRef.current;

  // canvas.width/height (the drawing buffer, fixed at 500x180) vs.
  // rect.width/height (the CSS-scaled displayed size, "width: 100%" - much
  // narrower than 500 on a phone) - without scaling, coordinates from a
  // narrower screen would land compressed into the buffer's left portion
  // instead of matching where the finger actually is.
  const pointerPos = (e) => {
    const canvas = getCanvas();
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const handlePointerDown = (e) => {
    const canvas = getCanvas();
    canvas.setPointerCapture(e.pointerId);
    const ctx = canvas.getContext('2d');
    const { x, y } = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    drawing.current = true;
  };

  const handlePointerMove = (e) => {
    if (!drawing.current) return;
    const canvas = getCanvas();
    const ctx = canvas.getContext('2d');
    const { x, y } = pointerPos(e);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000000';
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasInk.current) {
      hasInk.current = true;
      setHasInkState(true);
    }
  };

  const handlePointerUp = () => {
    drawing.current = false;
  };

  const clear = () => {
    const canvas = getCanvas();
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    hasInk.current = false;
    setHasInkState(false);
  };

  const init = () => {
    const canvas = getCanvas();
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  // Runs once on mount only (2026-08-23, real bug fixed - init() used to run
  // from the <canvas> element's own inline ref callback, e.g.
  // `ref={(el) => { ...; pad.init(); }}`. React treats an inline arrow
  // function as a NEW ref every render, so it re-fires on EVERY re-render of
  // the parent, not just mount - typing into the PIN field below calls
  // setPin(), which re-renders SignatureCapture, which re-fired init(),
  // which paints the canvas back to blank white, silently wiping out
  // whatever was already drawn. hasInk stayed true (only clear() resets it),
  // so the button stayed enabled and submitted a blank image - exactly the
  // "attachment arrived as a white square" bug report.
  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toPngDataUrl = () => getCanvas().toDataURL('image/png');

  return { canvasRef, hasInk: hasInkState, handlePointerDown, handlePointerMove, handlePointerUp, clear, toPngDataUrl };
}

// submitFn (volitelné, 2026-08-23 - první další volající je Docházka's
// překážka v práci) - když je zadané, nahrazuje výchozí volání
// captureSignature() na generický /signatures/capture. Umožňuje modulu mít
// vlastní endpoint, který PIN+podpis ověří a rovnou k tomu atomicky udělá
// svou vlastní doménovou akci (signObstacle() zapíše signature_id a
// rozhodnutí v jedné transakci) - bez toho by šlo jen podpis zachytit
// obecně a pak ho modul musel dohledávat/přiřazovat zvlášť.
// Signatura: (pin, signatureImageDataUrl, evidencePhotoDataUrl) => Promise<any>,
// návratová hodnota se předá do onCaptured() stejně jako výchozí id.
export default function SignatureCapture({ contextType, contextId = null, requirePhoto = false, submitFn = null, onCaptured }) {
  const pad = useSignaturePad();
  const photoInputRef = useRef(null);
  const [pin, setPin] = useState('');
  const [photoDataUrl, setPhotoDataUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Dvoukrokový formulář, přesná podoba na výslovné přání uživatele
  // (2026-08-23): krok 'draw' = velký podpis přes (na mobilu) skoro celou
  // obrazovku + dvě tlačítka přes celou šířku pod sebou ("Vymazat a znovu",
  // "Ověřit PINem"); krok 'pin' = číselník + jedno tlačítko přes celou
  // šířku ("Ověřit a odeslat") - žádný náhled podpisu na kroku 'pin', to už
  // je vidět přímo na plátně na kroku 'draw'. Canvas zůstává v DOM po celou
  // dobu (jen skrytý přes CSS, ne odmontovaný) - odmontování a nové
  // namontování by mohlo znovu narazit na stejnou třídu re-render bugu, co
  // už byla jednou opravena (viz useSignaturePad's vlastní komentář u
  // init()).
  const [step, setStep] = useState('draw');

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoDataUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const canVerify = pad.hasInk && (!requirePhoto || photoDataUrl);
  const canSubmit = PIN_PATTERN.test(pin) && !submitting;

  const handleRedo = () => {
    pad.clear();
    setPin('');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const result = submitFn
        ? await submitFn(pin, pad.toPngDataUrl(), photoDataUrl)
        : await captureSignature(contextType, contextId, pin, pad.toPngDataUrl(), photoDataUrl).then((r) => r.id);
      pad.clear();
      setPin('');
      setPhotoDataUrl(null);
      setStep('draw');
      onCaptured?.(result);
    } catch (e) {
      setError(e.message || 'Podpis se nepodařilo uložit.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack sx={{ gap: 2, height: '100%' }}>
      <Stack sx={{ gap: 1, display: step === 'draw' ? 'flex' : 'none', flex: 1, minHeight: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Podpis (nakreslete prstem nebo myší)
        </Typography>
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            touchAction: 'none',
            flex: 1,
            minHeight: { xs: 320, sm: 220 }
          }}
        >
          <canvas
            ref={pad.canvasRef}
            width={500}
            height={700}
            style={{ width: '100%', height: '100%', display: 'block', touchAction: 'none', cursor: 'crosshair' }}
            onPointerDown={pad.handlePointerDown}
            onPointerMove={pad.handlePointerMove}
            onPointerUp={pad.handlePointerUp}
            onPointerLeave={pad.handlePointerUp}
          />
        </Box>

        {requirePhoto && (
          <Stack sx={{ gap: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Fotka jako doklad podpisu (povinné u tohoto potvrzení)
            </Typography>
            <input ref={photoInputRef} type="file" accept="image/*" capture="environment" hidden onChange={handlePhotoChange} />
            <Button
              size="small"
              variant="outlined"
              onClick={() => photoInputRef.current?.click()}
              startIcon={<IconCamera style={{ fontSize: '1.1rem' }} />}
              sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
            >
              {photoDataUrl ? 'Fotka pořízena - vyfotit znovu' : 'Vyfotit'}
            </Button>
          </Stack>
        )}

        <Button
          fullWidth
          variant="outlined"
          onClick={handleRedo}
          disabled={!pad.hasInk}
          startIcon={<IconDelete style={{ fontSize: '1.1rem' }} />}
          sx={{ textTransform: 'none' }}
        >
          Vymazat a znovu
        </Button>
        <Button fullWidth variant="contained" onClick={() => setStep('pin')} disabled={!canVerify} sx={{ textTransform: 'none' }}>
          Ověřit PINem
        </Button>
      </Stack>

      {step === 'pin' && (
        <Stack sx={{ gap: 2 }}>
          {/* Vlastní číselník, žádný nativní <input> (2026-08-23, na výslovné
              přání uživatele) - viz components/PinKeypad.jsx, nahrazuje dřívější
              autoComplete="one-time-code" záplatu úplným odstraněním OS
              klávesnice a s ní spojeného autofillu/"uložit heslo" nabídky. */}
          <Stack sx={{ gap: 1, alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              PIN (potvrzení podpisu)
            </Typography>
            <PinKeypad value={pin} onChange={setPin} />
          </Stack>

          <Button fullWidth variant="contained" onClick={handleSubmit} disabled={!canSubmit} sx={{ textTransform: 'none' }}>
            {submitting ? 'Ukládám...' : 'Ověřit a odeslat'}
          </Button>
        </Stack>
      )}

      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}
